import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import Monnify from "monnify-js";
import { Bell, CheckCircle2, Download } from "lucide-react";

import { getInvoiceDetails, markInvoiceAsPaid, sendInvoiceReminder } from "src/api/invoice";
import { initiatePayment, verifyPayment } from "src/api/payments";
import { useDownloadInvoiceMutation } from "src/api/mutations";
import env from "src/config/env";
import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { Button } from "src/components/ui/button";
import { Skeleton } from "src/components/ui/skeleton";
import { DataTable, EmptyState, StatusBadge, type Column } from "src/components/common";
import { getCustomizationValue, renderCurrency, renderNumber, showSuccessToast } from "src/lib/utils";
import { invoiceStatusBadge, type InvoiceItem } from "src/types/invoice";

const monnify = new Monnify(env.REACT_APP_MONNIFY_API_KEY, env.REACT_APP_MONNIFY_CONTRACT_CODE);

interface InvoiceDetailModalProps {
	uuid: string | null;
	onOpenChange: (open: boolean) => void;
}

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
	<div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-1.5">
		<span className={strong ? "tw-text-[13.5px] tw-font-semibold tw-text-text" : "tw-text-[13px] tw-text-text-soft"}>{label}</span>
		<span className={strong ? "tw-text-[15px] tw-font-bold tw-text-text" : "tw-text-[13.5px] tw-text-text"}>{value}</span>
	</div>
);

const Field = ({ label, value }: { label: string; value?: string }) => (
	<div className="tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-px-3 tw-py-2.5">
		<div className="tw-mb-1 tw-text-[11.5px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-text-soft">{label}</div>
		<div className="tw-break-words tw-text-[13.5px] tw-text-text">{value || "—"}</div>
	</div>
);

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "MMM dd, yyyy");
};

/** Invoice detail as a modal (decision D2). `/invoices/view/:uuid` redirects here via `?invoice=<uuid>`. */
const InvoiceDetailModal = ({ uuid, onOpenChange }: InvoiceDetailModalProps) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const { data, isLoading, refetch } = useQuery({
		queryKey: ["invoice", uuid],
		queryFn: () => getInvoiceDetails(uuid as string),
		enabled: Boolean(uuid),
	});

	const invoice = data?.data;

	const { mutate: verifyMutate } = useMutation(verifyPayment, { onSuccess: () => refetch() });

	const { mutate: initiateMutate } = useMutation(initiatePayment, {
		onSuccess: (payload) => {
			monnify.initializePayment({
				...payload,
				onClose: (response: { status?: string; paymentReference?: string }) => {
					if (response.status === "SUCCESS") {
						refetch();
						verifyMutate({ reference: response.paymentReference as string, business_uuid: user?.business_uuid });
					}
				},
			});
		},
	});

	const { mutate: sendReminder, isLoading: isReminderSending } = useMutation(sendInvoiceReminder, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Reminder sent successfully");
			queryClient.invalidateQueries(["invoices"]);
		},
	});

	const { mutate: markPaid, isLoading: isMarkingPaid } = useMutation(markInvoiceAsPaid, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Marked as paid");
			queryClient.invalidateQueries(["invoices"]);
			refetch();
		},
	});

	const { mutate: downloadInvoice } = useDownloadInvoiceMutation();

	// `?initiatePayment=1` (set when an invoice is created) kicks off payment once, immediately.
	const hasInitiated = useRef(false);
	useEffect(() => {
		if (
			searchParams.get("initiatePayment") !== "1" ||
			hasInitiated.current ||
			!invoice ||
			invoice.status === "paid" ||
			invoice.payment_method !== "transfer"
		) {
			return;
		}

		hasInitiated.current = true;
		initiateMutate({ customer_id: invoice.customer_id, invoice_id: invoice.id, business_uuid: user?.business_uuid });

		const next = new URLSearchParams(searchParams);
		next.delete("initiatePayment");
		setSearchParams(next, { replace: true });
	}, [searchParams, invoice, initiateMutate, user?.business_uuid, setSearchParams]);

	const itemColumns: Column<InvoiceItem>[] = [
		{ key: "item", header: "Item", primary: true, cell: (row) => row.product?.name ?? "—" },
		{ key: "qty", header: "Qty", cell: (row) => renderNumber(row.qty ?? 0) },
		{ key: "unit", header: "Unit Price", cell: (row) => renderCurrency(row.product_price ?? 0) },
		{ key: "total", header: "Total", className: "tw-text-right", cell: (row) => renderCurrency(row.amount ?? 0) },
	];

	return (
		<CustomModal
			open={Boolean(uuid)}
			onOpenChange={onOpenChange}
			size="normal"
			title={
				isLoading ? (
					<Skeleton className="tw-h-5 tw-w-40" />
				) : (
					<span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5">
						<span>
							{invoiceLabel} {invoice?.invoice_id ?? ""}
						</span>
						{invoice?.status && <StatusBadge status={invoiceStatusBadge(invoice.status)} />}
					</span>
				)
			}
			footer={
				invoice && (
					<div className="tw-flex tw-flex-wrap tw-gap-2">
						{invoice.status !== "paid" && (
							<CustomConfirmDialog
								trigger={
									<Button size="sm" disabled={isReminderSending} className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
										<Bell className="tw-h-4 tw-w-4" />
										Send Reminder
									</Button>
								}
								mutateFn={() => sendReminder({ uuid: invoice.uuid })}
							/>
						)}
						{invoice.status !== "paid" && invoice.payment_method === "transfer" && (
							<CustomConfirmDialog
								trigger={
									<Button size="sm" variant="outline">
										Initiate Payment
									</Button>
								}
								mutateFn={() =>
									initiateMutate({
										customer_id: invoice.customer_id,
										invoice_id: invoice.id,
										business_uuid: user?.business_uuid,
									})
								}
							/>
						)}
						{invoice.status !== "paid" && invoice.payment_method === "offline" && (
							<CustomConfirmDialog
								trigger={
									<Button size="sm" disabled={isMarkingPaid} variant="outline" className="tw-gap-1.5">
										<CheckCircle2 className="tw-h-4 tw-w-4" />
										Mark as Paid
									</Button>
								}
								mutateFn={() => markPaid({ uuid: invoice.uuid })}
							/>
						)}
						{invoice.status === "paid" && (
							<Button size="sm" variant="outline" className="tw-gap-1.5" onClick={() => downloadInvoice(invoice.uuid)}>
								<Download className="tw-h-4 tw-w-4" />
								Download
							</Button>
						)}
					</div>
				)
			}
		>
			{isLoading ? (
				<div className="tw-space-y-4">
					<Skeleton className="tw-h-24" />
					<Skeleton className="tw-h-40" />
				</div>
			) : !invoice ? (
				<EmptyState title={`${invoiceLabel} not found`} description="This record may have been removed." />
			) : (
				<div className="tw-space-y-6">
					<div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
						<Field
							label={`${customerLabel} Name`}
							value={[invoice.customer?.first_name, invoice.customer?.last_name].filter(Boolean).join(" ")}
						/>
						<Field label="Phone" value={invoice.customer?.phone} />
						<Field label="Email" value={invoice.customer?.email} />
						<Field label="Due Date" value={formatDate(invoice.due_date)} />
						<Field label="Payment Method" value={invoice.payment_method} />
						<Field label={`${invoiceLabel} Number`} value={invoice.invoice_id} />
					</div>

					<section>
						<h4 className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">Items</h4>
						<DataTable
							data={invoice.items ?? []}
							columns={itemColumns}
							rowKey={(row, index) => row.id ?? index}
							rowsPerPageOptions={[10, 25, "All"]}
							emptyTitle="No items"
						/>
					</section>

					<section className="tw-rounded-lg tw-border tw-border-border tw-bg-bg tw-px-4 tw-py-3">
						<Row label="Subtotal" value={renderCurrency(invoice.amount ?? 0)} />
						<Row label="Discount" value={renderCurrency(invoice.discount_amount ?? 0)} />
						<Row label="VAT" value={renderCurrency(invoice.vat ?? 0)} />
						<div className="tw-my-1.5 tw-h-px tw-bg-border" />
						<Row label="Total" value={renderCurrency(invoice.total_amount ?? 0)} strong />
						<Row label="Balance" value={renderCurrency(invoice.balance ?? 0)} />
					</section>
				</div>
			)}
		</CustomModal>
	);
};

export default InvoiceDetailModal;
